import { createContext, useContext, useMemo, type CSSProperties, type ReactNode } from 'react';
import type { Block, Quote } from '@/types';
import { A4_LANDSCAPE } from '@/constants/pageFormat';

export type QuoteDocumentMode = 'edit' | 'preview' | 'web' | 'print';

export interface QuoteRenderInput {
  blocks: Block[];
  layout: Record<string, unknown>;
  variables: Record<string, string>;
  locale: 'nl' | 'en';
  hash: string;
}

function stableSerialize(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (value instanceof Date) return JSON.stringify(value.toISOString());
  if (Array.isArray(value)) return `[${value.map(stableSerialize).join(',')}]`;

  return `{${Object.entries(value as Record<string, unknown>)
    .filter(([, entry]) => entry !== undefined)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, entry]) => `${JSON.stringify(key)}:${stableSerialize(entry)}`)
    .join(',')}}`;
}

function stableHash(value: unknown): string {
  const input = stableSerialize(value);
  let hash = 2166136261;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `qr-${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

export function createQuoteRenderInput(
  source: Pick<Quote, 'blocks' | 'layoutConfig' | 'variableMap' | 'publicLocale'>,
): QuoteRenderInput {
  const input = {
    blocks: source.blocks,
    layout: source.layoutConfig ?? {},
    variables: source.variableMap ?? {},
    locale: source.publicLocale === 'en' ? ('en' as const) : ('nl' as const),
  };
  return { ...input, hash: stableHash(input) };
}

const QuoteDocumentModeContext = createContext({
  mode: 'web' as QuoteDocumentMode,
  inlineEditable: false,
});

export function useQuoteDocumentMode(): QuoteDocumentMode {
  return useContext(QuoteDocumentModeContext).mode;
}

export function useQuoteDocumentEditable(): boolean {
  const { mode, inlineEditable } = useContext(QuoteDocumentModeContext);
  return mode === 'edit' || (mode === 'preview' && inlineEditable);
}

interface QuoteDocumentProps {
  mode: QuoteDocumentMode;
  /** Builder split view: editable text with the customer product projection. */
  inlineEditable?: boolean;
  input: QuoteRenderInput;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}

/**
 * Gedeeld documentcontract voor builder, preview, klantlink en PDF. De inhoud
 * is één vaste render-input; de modus mag alleen bediening en responsive gedrag
 * veranderen, nooit opgeslagen inhoud of documentmaten.
 */
export default function QuoteDocument({
  mode,
  inlineEditable = false,
  input,
  children,
  className = 'contents',
  style,
}: QuoteDocumentProps) {
  const context = useMemo(() => ({ mode, inlineEditable }), [mode, inlineEditable]);
  const documentStyle = useMemo(
    () =>
      ({
        '--quote-page-width': `${A4_LANDSCAPE.width}px`,
        '--quote-page-height': `${A4_LANDSCAPE.height}px`,
        '--quote-content-height': `${A4_LANDSCAPE.contentHeight}px`,
        '--quote-page-padding': `${A4_LANDSCAPE.padding}px`,
        ...style,
      }) as CSSProperties,
    [style],
  );

  return (
    <QuoteDocumentModeContext.Provider value={context}>
      <div
        className={className}
        style={documentStyle}
        data-quote-document
        data-quote-document-mode={mode}
        data-quote-render-hash={input.hash}
      >
        {children}
      </div>
    </QuoteDocumentModeContext.Provider>
  );
}
